import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useOutletsOfCompanies,
  useRoles,
  useSaveUser,
  useServiceTypesOfCompanies,
} from '@/api/hooks'
import type { AppUser } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

type Draft = {
  companyId: string
  companyIds: string[]
  serviceCodes: string[]
  username: string
  fullName: string
  email: string
  mobile: string
  employeeCode: string
  department: string
  primaryOutletId: string
  mfaRequired: boolean
  roleCodes: string[]
  outletIds: string[]
  password: string
}

const BLANK: Draft = {
  companyId: '',
  companyIds: [],
  serviceCodes: [],
  username: '',
  fullName: '',
  email: '',
  mobile: '',
  employeeCode: '',
  department: '',
  primaryOutletId: '',
  mfaRequired: false,
  roleCodes: [],
  outletIds: [],
  password: '',
}

/**
 * Creates or corrects a user: who they are, what they may do, and which outlets
 * they may do it in.
 *
 * <p>Roles carry the permissions; outlet access decides what those permissions
 * reach. Someone with no outlet ticked sees every outlet, which is what a head
 * office role wants and a branch role does not.</p>
 *
 * <p>The user list is the one thing kept across companies rather than inside
 * one, so each person is mapped to the company they work for. Everything they
 * then see - customers, orders, takings - is that company's and no other's.</p>
 */
export function UserDialog({
  open,
  user,
  onClose,
}: {
  open: boolean
  user?: AppUser | null
  onClose: () => void
}) {
  const { data: roles } = useRoles()
  const { data: companies } = useCompanies()
  const save = useSaveUser()
  const can = useAuthStore((state) => state.can)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId) ?? ''
  const [draft, setDraft] = useState<Draft>(BLANK)

  useEffect(() => {
    if (!open) return
    setDraft(
      user
        ? {
            companyId: user.companyId,
            // Home first, always: the list is read that way on screen and
            // written back that way when it is saved.
            companyIds: [
              user.companyId,
              ...(user.companyIds ?? []).filter((id) => id !== user.companyId),
            ],
            serviceCodes: user.serviceCodes ?? [],
            username: user.username,
            fullName: user.fullName,
            email: user.email ?? '',
            mobile: user.mobile ?? '',
            employeeCode: user.employeeCode ?? '',
            department: user.department ?? '',
            primaryOutletId: user.primaryOutletId ?? '',
            mfaRequired: user.mfaRequired,
            roleCodes: user.roleCodes,
            outletIds: user.outletIds,
            password: '',
          }
        : {
            ...BLANK,
            companyId: ownCompanyId,
            companyIds: ownCompanyId ? [ownCompanyId] : [],
          },
    )
  }, [open, user, ownCompanyId])

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))

  // Which company they work for. A platform administrator may set somebody up
  // in another one, and may move an existing person, which clears the mapping
  // that went with the old company: outlets and roles both belong to it.
  const canChooseCompany = can('platform.admin') && (companies ?? []).length > 1
  // The outlets of every business this person may work in - not the ones the
  // person doing the editing happens to work in, and not only the home
  // company's: somebody in two businesses has to be mappable to branches in
  // both.
  const mappedCompanies = draft.companyIds.length > 0
    ? draft.companyIds
    : [draft.companyId || ownCompanyId].filter(Boolean)
  const { outlets } = useOutletsOfCompanies(mappedCompanies)
  // What the businesses sell, so somebody can be given one line of it. A
  // takeaway counter clerk has no business quoting a wedding.
  const { services: allServices } = useServiceTypesOfCompanies(mappedCompanies)

  /**
   * The lines that can still be handed out.
   *
   * <p>A withdrawn service is not one to grant: the business has stopped
   * selling it. One somebody was already given stays on the list, so an old
   * grant reads as a name and can be taken off deliberately.</p>
   *
   * <p>Kept unique by code, because a code is what a grant is recorded as and
   * two businesses in a group carry the same five. Where they disagree about
   * one - withdrawn here, sold there - the live one is the one to show.</p>
   */
  const services = (() => {
    const byCode = new Map<string, (typeof allServices)[number]>()
    allServices.forEach((service) => {
      const seen = byCode.get(service.code)
      if (!seen || (!seen.active && service.active)) byCode.set(service.code, service)
    })
    return [...byCode.values()].filter(
      (service) => service.active || draft.serviceCodes.includes(service.code),
    )
  })()
  const nameOfCompany = (id: string) =>
    companies?.find((company) => company.id === id)?.name ?? id

  /**
   * The businesses this person works in, the first of them their home.
   *
   * <p>Home is where they land at sign-in and what a company header the
   * account cannot reach falls back to, so there has to be exactly one - but
   * it is the first of the same list rather than a question of its own, since
   * naming the business somebody works for and then naming it again to say
   * they may work in it is asking twice.</p>
   */
  const chooseCompanies = (value: string[]) => {
    const home = value[0] ?? ''
    const moved = home !== draft.companyId
    set({
      companyId: home,
      companyIds: value,
      // Roles and the home outlet belong to a company, so a change of home
      // cannot carry the old one's across.
      roleCodes: moved ? [] : draft.roleCodes,
      primaryOutletId: moved ? '' : draft.primaryOutletId,
      // An outlet of a business they are no longer in is not theirs to work
      // in either. Dropped here rather than left to be noticed later as
      // access nobody granted.
      outletIds: draft.outletIds.filter((id) => {
        const outlet = outlets.find((candidate) => candidate.id === id)
        return !outlet || value.includes(outlet.companyId)
      }),
    })
  }

  // A withdrawn outlet is not somewhere anybody starts working, so it is not
  // offered. One already mapped stays on the list: an old mapping should be
  // visible and taken off deliberately, not vanish and leave access that
  // nothing on the screen accounts for.
  //
  // Kept unique by id as well. An outlet one business shares with another is
  // answered for both, and the two lists are simply run together, so a shared
  // branch arrived twice and was offered twice.
  const openOutlets = [
    ...new Map(
      outlets
        .filter(
          (outlet) =>
            outlet.active ||
            draft.outletIds.includes(outlet.id) ||
            outlet.id === draft.primaryOutletId,
        )
        .map((outlet) => [outlet.id, outlet]),
    ).values(),
  ]

  /**
   * The outlets to choose from, under the business each belongs to.
   *
   * <p>In the order the businesses were named, so the home company's branches
   * come first. A person in two of them was otherwise reading one flat run of
   * branch names with no way to tell which business a name belonged to - and
   * two businesses in a group will happily both have a "Main outlet".</p>
   */
  const outletGroups = mappedCompanies
    .map((companyId) => ({
      companyId,
      name: nameOfCompany(companyId),
      list: openOutlets.filter((outlet) => outlet.companyId === companyId),
    }))
    .filter((group) => group.list.length > 0)

  const companyOf = (outletId: string) =>
    outlets.find((outlet) => outlet.id === outletId)?.companyName ?? ''
  const labelOf = (outletId: string) => {
    const outlet = outlets.find((candidate) => candidate.id === outletId)
    return outlet ? `${outlet.code} — ${outlet.name}` : outletId
  }

  // A new user needs a way in; an existing one keeps the password they have.
  const passwordValid = user ? true : draft.password.length >= 10
  // Somebody has to belong somewhere: the home company is what a request with
  // no usable company header falls back to.
  const valid =
    Boolean(draft.username.trim() && draft.fullName.trim() && draft.companyId) && passwordValid

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{user ? `Edit ${user.fullName}` : 'New user'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {canChooseCompany ? (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={(companies ?? []).map((company) => company.id)}
                value={draft.companyIds}
                onChange={(_event, value) => chooseCompanies(value)}
                getOptionLabel={nameOfCompany}
                renderTags={(value, getTagProps) =>
                  value.map((id, index) => (
                    <Chip
                      size="small"
                      // The first is home, and looks it. Asked as one list
                      // rather than two, so nobody has to name the same
                      // business twice to say a person works in it.
                      color={index === 0 ? 'primary' : 'default'}
                      label={
                        index === 0 ? `${nameOfCompany(id)} — signs in here` : nameOfCompany(id)
                      }
                      {...getTagProps({ index })}
                      key={id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Companies"
                    helperText={
                      draft.companyIds.length === 0
                        ? 'Name at least one. Everything this person sees belongs to it.'
                        : draft.companyIds.length === 1
                          ? 'Add another to let one person work in two businesses without a second account'
                          : `${draft.companyIds.length} businesses; they switch between them from the header. The first is where they sign in — take it off to hand that to another.`
                    }
                  />
                )}
              />
            </Grid>
          ) : null}
          {/* The first company named is where they sign in, so taking a
              different one to the front moves them. Consequential enough to be
              said out loud rather than left to be noticed once their outlets
              have gone. */}
          {user && draft.companyId && draft.companyId !== user.companyId ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                Saving moves {user.fullName} to {nameOfCompany(draft.companyId)}. The outlets they
                were mapped to and any role their old company wrote for itself are left behind;
                built-in roles move with them.
              </Alert>
            </Grid>
          ) : null}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Username"
              value={draft.username}
              onChange={(event) => set({ username: event.target.value.trim() })}
              disabled={Boolean(user)}
              helperText={user ? 'Usernames cannot change' : 'What they sign in with'}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Full name"
              value={draft.fullName}
              onChange={(event) => set({ fullName: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Employee code"
              value={draft.employeeCode}
              onChange={(event) => set({ employeeCode: event.target.value })}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email"
              value={draft.email}
              onChange={(event) => set({ email: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Mobile"
              value={draft.mobile}
              onChange={(event) => set({ mobile: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Department"
              value={draft.department}
              onChange={(event) => set({ department: event.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={(roles ?? []).map((role) => role.code)}
              value={draft.roleCodes}
              onChange={(_event, value) => set({ roleCodes: value })}
              getOptionLabel={(code) => roles?.find((role) => role.code === code)?.name ?? code}
              renderTags={(value, getTagProps) =>
                value.map((code, index) => (
                  <Chip
                    size="small"
                    label={roles?.find((role) => role.code === code)?.name ?? code}
                    {...getTagProps({ index })}
                    key={code}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles"
                  helperText="What they may do. Someone with no role can sign in and see nothing."
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={services.map((service) => service.code)}
              value={draft.serviceCodes}
              onChange={(_event, value) => set({ serviceCodes: value })}
              getOptionLabel={(code) =>
                services.find((service) => service.code === code)?.name ?? code
              }
              renderTags={(value, getTagProps) =>
                value.map((code, index) => (
                  <Chip
                    size="small"
                    label={services.find((s) => s.code === code)?.name ?? code}
                    {...getTagProps({ index })}
                    key={code}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lines of business they handle"
                  helperText={
                    draft.serviceCodes.length === 0
                      ? 'None ticked — they handle everything this business sells'
                      : `${draft.serviceCodes.length} of them; they cannot raise an enquiry for the rest`
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Home outlet"
              value={draft.primaryOutletId}
              onChange={(event) => set({ primaryOutletId: event.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {/* Headed by business, the same way the list beside it is. A
                  subheader is not selectable, so the names below one read as
                  that business's branches without repeating it on every row. */}
              {outletGroups.flatMap((group) => [
                <ListSubheader key={`group-${group.companyId}`}>{group.name}</ListSubheader>,
                ...group.list.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.id}>
                    {outlet.code} — {outlet.name}
                  </MenuItem>
                )),
              ])}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              // Already in business order, which is what groupBy needs: it
              // heads a run of adjacent options, so an unsorted list would
              // print the same business as a heading more than once.
              options={outletGroups.flatMap((group) => group.list.map((outlet) => outlet.id))}
              value={draft.outletIds}
              onChange={(_event, value) => set({ outletIds: value })}
              // Grouped by business, so a person mapped to two of them can see
              // which branch belongs to which.
              groupBy={(id) => companyOf(id)}
              getOptionLabel={labelOf}
              renderTags={(value, getTagProps) =>
                value.map((id, index) => (
                  <Chip
                    size="small"
                    label={labelOf(id)}
                    {...getTagProps({ index })}
                    key={id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Outlets they may work in"
                  helperText={
                    draft.outletIds.length === 0
                      ? 'None ticked — they see every outlet of the businesses above'
                      : `${draft.outletIds.length} outlet(s); everything else is hidden from them`
                  }
                />
              )}
            />
          </Grid>

          {user ? null : (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="password"
                label="Password"
                value={draft.password}
                onChange={(event) => set({ password: event.target.value })}
                helperText="At least 10 characters. They should change it once they are in."
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.mfaRequired}
                  onChange={(event) => set({ mfaRequired: event.target.checked })}
                />
              }
              label="Require a second factor"
            />
          </Grid>

          {draft.roleCodes.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                No role chosen. They will be able to sign in but every screen will be closed to them.
              </Alert>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: user?.id,
                body: {
                  companyId: draft.companyId || undefined,
                  companyIds: draft.companyIds.length ? draft.companyIds : undefined,
                  username: draft.username,
                  fullName: draft.fullName.trim(),
                  email: draft.email || undefined,
                  mobile: draft.mobile || undefined,
                  employeeCode: draft.employeeCode || undefined,
                  department: draft.department || undefined,
                  primaryOutletId: draft.primaryOutletId || undefined,
                  mfaRequired: draft.mfaRequired,
                  roleCodes: draft.roleCodes,
                  outletIds: draft.outletIds,
                  serviceCodes: draft.serviceCodes,
                  password: draft.password || undefined,
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          {user ? 'Save changes' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
